import React from "react";

type TimelineViewMonthlyProps = {
    shippingData: any[];
    currentYear: number;
};

// Helper function to check if a date falls within a specific month
function isDateInMonth(dateString: string, monthLabel: string): boolean {
    if (!dateString) return false;

    // Parse the month label (e.g., "Jan 2024")
    const monthParts = monthLabel.split(" ");
    if (monthParts.length !== 2) {
        console.error("Invalid month format. Expected format: 'Jan 2024'");
        return false;
    }

    const monthName = monthParts[0];
    const year = parseInt(monthParts[1]);

    // Map month names to numbers (shortened form)
    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const monthIndex = monthNames.indexOf(monthName);
    if (monthIndex === -1) {
        console.error("Invalid month name:", monthName);
        return false;
    }

    // Parse the date string
    const [dateYear, dateMonthStr, dateDay] = dateString.split("-").map(Number);
    const inputDate = new Date(dateYear, dateMonthStr - 1, dateDay);

    // Check if the date's month and year match
    return (
        inputDate.getMonth() === monthIndex && inputDate.getFullYear() === year
    );
}

// Helper function to get current month
function isCurrentMonth(monthLabel: string): boolean {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const monthParts = monthLabel.split(" ");
    const monthName = monthParts[0];
    const year = parseInt(monthParts[1]);

    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];

    const monthIndex = monthNames.indexOf(monthName);

    return monthIndex === currentMonth && year === currentYear;
}

// Helper function to check if an item is late for a specific month
function isItemLateForMonth(row: any, monthLabel: string): boolean {
    if (!row.date_planned || row.effective_date) return false;

    const monthParts = monthLabel.split(" ");
    const monthName = monthParts[0];
    const year = parseInt(monthParts[1]);

    const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];
    const monthIndex = monthNames.indexOf(monthName);

    if (monthIndex === -1) return false;

    const plannedDate = new Date(row.date_planned);
    const currentDate = new Date();

    // Get the start and end of the month
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = new Date(year, monthIndex + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);

    // If planned date is before or during this month AND we're past this month AND item not shipped/delivered
    if (
        plannedDate <= monthEnd &&
        currentDate > monthEnd &&
        !row.shipped_date &&
        !row.effective_date
    ) {
        return true;
    }

    return false;
}

// Main chip rendering function for monthly view
const renderChips = (row: any, monthLabel: string) => {
    const chips = [];

    // Check for ETA (date_planned in this month)
    const isETASameMonth = isDateInMonth(row.date_planned, monthLabel);

    // Check for Ship Arrived (shipped_date in this month)
    const isShipArrivedSameMonth =
        row.shipped_date && isDateInMonth(row.shipped_date, monthLabel);

    // Check for Delivery (effective_date in this month)
    const isDeliverySameMonth =
        row.effective_date &&
        !row.is_dropship &&
        isDateInMonth(row.effective_date, monthLabel);

    // Check for Inspection (dropship_inspection_date in this month)
    const isInspectionSameMonth =
        row.dropship_inspection_date &&
        isDateInMonth(row.dropship_inspection_date, monthLabel);

    // Check for Late status
    const isLate = isItemLateForMonth(row, monthLabel);

    // Priority logic: ETA and Late should not appear together in same cell
    if (isETASameMonth && isLate) {
        // If both ETA and Late would appear, show only Late (higher priority for overdue items)
        chips.push(
            <span
                key="eta"
                className="badge"
                style={{
                    backgroundColor: "#22cc00ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    margin: "1px",
                }}
            >
                ETA
            </span>
        );
    } else {
        // Show ETA if not conflicting with Late
        if (isETASameMonth) {
            chips.push(
                <span
                    key="eta"
                    className="badge"
                    style={{
                        backgroundColor: "#22cc00ff",
                        fontWeight: "700",
                        fontSize: "0.7rem",
                        color: "#333",
                        margin: "1px",
                    }}
                >
                    ETA
                </span>
            );
        }

        // Show Late if not conflicting with ETA
        if (isLate) {
            chips.push(
                <span
                    key="late"
                    className="badge"
                    style={{
                        backgroundColor: "#f57242ff",
                        fontWeight: "700",
                        fontSize: "0.7rem",
                        color: "#333",
                        display: "inline",
                        margin: "1px",
                    }}
                >
                    Late
                </span>
            );
        }
    }

    // Add other chips (these don't conflict with ETA/Late)
    if (isShipArrivedSameMonth) {
        chips.push(
            <span
                key="ship-arrived"
                className="badge"
                style={{
                    backgroundColor: "#22cc00ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    display: "inline",
                    margin: "1px",
                }}
            >
                Ship Arrived
            </span>
        );
    }

    if (isDeliverySameMonth) {
        chips.push(
            <span
                key="delivery"
                className="badge"
                style={{
                    backgroundColor: "#22cc00ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    display: "inline",
                    margin: "1px",
                }}
            >
                Receipt
            </span>
        );
    }

    if (isInspectionSameMonth) {
        chips.push(
            <span
                key="inspection"
                className="badge"
                style={{
                    backgroundColor: "#22cc00ff",
                    fontWeight: "700",
                    fontSize: "0.7rem",
                    color: "#333",
                    display: "inline",
                    margin: "1px",
                }}
            >
                Inspected
            </span>
        );
    }

    return chips;
};

const TimelineViewMonthly = (props: TimelineViewMonthlyProps) => {
    // Generate all 12 months for the current year
    const generateMonthColumns = (): string[] => {
        const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];

        return monthNames.map((month) => `${month} ${props.currentYear}`);
    };

    const monthColumns = generateMonthColumns();

    return (
        <table
            className="table table-hover mb-0"
            style={{
                borderRight: "1px solid #efefef",
            }}
        >
            <thead
                style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#0d6efd",
                    color: "white",
                    zIndex: 10,
                }}
            >
                <tr>
                    {monthColumns.map((monthName: string, idx) => (
                        <th
                            key={idx}
                            className="fw-semibold text-center"
                            style={{
                                fontSize: "0.7rem",
                                color: "#6c757d",
                                padding: "0.6rem",
                                borderBottom: "2px solid #dee2e6",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {monthName}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {props.shippingData.map((row: any, idx) => (
                    <tr
                        key={idx}
                        style={{
                            borderBottom: "1px solid #f0f0f0",
                            height: 50,
                        }}
                    >
                        {monthColumns.map((monthName, monthIdx) => (
                            <td
                                key={monthIdx}
                                className="text-center"
                                style={{
                                    padding: "0.5rem",
                                    backgroundColor: isCurrentMonth(monthName)
                                        ? "#6bb6deff"
                                        : "#fff",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        overflow: "visible",
                                    }}
                                >
                                    {renderChips(row, monthName)}
                                </div>
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default TimelineViewMonthly;
